import { ContextualHint } from '@src/components/common/ContextualHint';

import type { FC, ReactNode } from 'react';

/**
 * PLAT-11980 — the Transcript & Languages tab moved from the end of Content
 * Studio's tab bar to second place (PLAT-11877). Someone who learnt the old
 * place looks right and finds nothing, so the first time they open Content
 * Studio after the move a coach mark under the tab says where it went. Per
 * person, not per story: closed once, or the tab clicked once, it is gone for
 * this browser. See "Contextual Hints" in docs/design-system.md.
 */
export const TRANSCRIPT_TAB_MOVED_HINT_KEY = 'transcript-languages-tab-moved';

/**
 * PLAT-11991 — the notice names the tab the way the tab bar does for this
 * showcase (`TRANSCRIPT_TAB_LABELS`).
 */
export const TRANSCRIPT_TAB_MOVED_HINT_TEXT = {
  withLanguages:
    'Transcript and Languages have moved! You can now find them here.',
  withoutLanguages: 'Transcript has moved! You can now find it here.',
} as const;

type Props = {
  /** The host's gate — false where the tab is not the user's to find. */
  show: boolean;
  /** Whether this showcase has languages (`useShowcaseHasLanguages`). */
  hasLanguages: boolean;
  /** The tab the hint points at. */
  children: ReactNode;
};

export const TranscriptTabMovedHint: FC<Props> = ({
  show,
  hasLanguages,
  children,
}) => (
  <ContextualHint
    hintKey={TRANSCRIPT_TAB_MOVED_HINT_KEY}
    show={show}
    placement="bottom-start"
    text={
      hasLanguages
        ? TRANSCRIPT_TAB_MOVED_HINT_TEXT.withLanguages
        : TRANSCRIPT_TAB_MOVED_HINT_TEXT.withoutLanguages
    }
    testId="transcript-tab-moved-hint"
  >
    {children}
  </ContextualHint>
);

export default TranscriptTabMovedHint;
